import { CopyPlus, FileUp, GitCompare, RefreshCw, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, SelectField, StatusMessage } from '@krp/ui-kit'

import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { WarehouseSelect } from '../../structure/ui/WarehouseSelect'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import type { FillMode, SupplySection } from '../model/types'
import type { SupplyActions, SupplyVm } from '../model/viewModels'
import { Commands } from './Commands'
import { StatusBadge } from './StatusBadge'
import '../../../shared/ui/onec.css'
import '../../pricing/ui/onec-ui.css'
import './supplies.css'

const SECTIONS: readonly { value: SupplySection; label: string }[] = [
  { value: 'lines', label: 'Строки' },
  { value: 'allocation', label: 'Распределение' },
  { value: 'prices', label: 'Цены' },
  { value: 'posting', label: 'Проведение' },
  { value: 'history', label: 'История' },
]

const FILL_MODES: readonly { value: FillMode; label: string }[] = [
  { value: 'Append', label: 'Добавить строки' },
  { value: 'Merge', label: 'Объединить с имеющимися' },
  { value: 'Replace', label: 'Заменить все строки' },
]

/**
 * Окно поставки базы окна (rid + id в адресе). Смена «выбранной базы» где-то
 * ещё это окно не перепривязывает: его база — в его адресе. Шапка правится
 * черновиком с явным «Сохранить»; строки и распределение сохраняются сами
 * (debounce), конфликт версии показывает серверное значение рядом с набранным.
 */
export function SupplyScreen({ vm, actions, children }: { vm: SupplyVm; actions: SupplyActions; children?: ReactNode }) {
  useUnsavedGuard(vm.unsaved)
  const header = vm.header
  if (header === null || vm.summary === null) {
    return (
      <section className="onec-screen" aria-label="Поставка 1С">
        <OneCQueryState query={vm.query} emptyText="Поставка не найдена" onRetry={actions.reload} />
      </section>
    )
  }
  const locked = !vm.editable
  return (
    <section className="onec-screen" aria-label={`Поставка ${header.number}`}>
      <div className="onec-toolbar">
        <h2>Поставка {header.number}</h2>
        <StatusBadge badge={header.badge} />
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
        <Button variant="ghost" icon={<CopyPlus size={16} aria-hidden="true" />} onClick={actions.clone}>
          Копия
        </Button>
        <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} onClick={actions.remove} disabled={locked || vm.deleted}>
          Удалить
        </Button>
        <Button variant="ghost" onClick={actions.close}>
          Закрыть
        </Button>
      </div>
      {vm.query.status !== 'ready' && vm.query.status !== 'loading' && (
        <OneCQueryState query={vm.query} emptyText="Поставка не найдена" onRetry={actions.reload} />
      )}
      {vm.deleted && <StatusMessage kind="empty" title="Поставка удалена" text="История поставки сохранена на сервере." />}
      {locked && (
        <p className="onec-banner" role="status">
          Поставка проводится или уже проведена — правка закрыта. Разделы «Проведение» и «История» показывают, что произошло.
        </p>
      )}

      <div className="onec-section" aria-label="Шапка поставки">
        <div className="onec-section__head">
          <strong>{header.supplierName}</strong>
          <span className="onec-muted">
            изменено {header.updatedAt}
            {header.updatedBy !== null ? ` · ${header.updatedBy}` : ''}
          </span>
        </div>
        <div className="onec-grid">
          <InputField
            label="Дата документа"
            type="date"
            value={header.fields.documentDate.value}
            onChange={(value) => actions.headerChanged('documentDate', value)}
            disabled={locked}
            note={header.fields.documentDate.changed ? `изменено · на сервере ${header.fields.documentDate.server}` : null}
          />
          <InputField
            label="Номер накладной поставщика"
            value={header.fields.supplierInvoiceNumber.value}
            onChange={(value) => actions.headerChanged('supplierInvoiceNumber', value)}
            disabled={locked}
            note={header.fields.supplierInvoiceNumber.changed ? `изменено · на сервере «${header.fields.supplierInvoiceNumber.server}»` : null}
          />
          <WarehouseSelect
            label="Склад приёмки"
            value={header.fields.warehouseKey.value}
            pick={header.warehousePick}
            placeholder="Не выбран"
            extra={header.warehouseExtra}
            onChange={(value) => actions.headerChanged('warehouseKey', value)}
            onRetry={actions.reloadWarehouses}
          />
          <InputField
            label="Комментарий"
            value={header.fields.comment.value}
            onChange={(value) => actions.headerChanged('comment', value)}
            disabled={locked}
            note={header.fields.comment.changed ? 'изменено' : null}
          />
        </div>
        {header.stale && (
          <div className="onec-banner" role="alert">
            <GitCompare size={18} aria-hidden="true" />
            <p>
              Поставку изменили после начала вашей правки. Рядом с полями — значения на сервере. Оставьте свои значения
              поверх новой версии или откажитесь от правки.
            </p>
          </div>
        )}
        {header.problem !== null && <p className="onec-problem">{header.problem}</p>}
        {header.dirty && (
          <div className="onec-actions">
            <Button variant="primary" onClick={actions.saveHeader} disabled={locked || header.problem !== null}>
              Сохранить шапку
            </Button>
            {header.stale && (
              <Button variant="secondary" onClick={actions.keepMineHeader}>
                Оставить мои значения
              </Button>
            )}
            <Button variant="ghost" onClick={actions.discardHeader}>
              Отменить правку
            </Button>
          </div>
        )}
      </div>

      <dl className="onec-summary" aria-label="Сводка">
        <div>
          <dt>Строк</dt>
          <dd>{vm.summary.lines}</dd>
        </div>
        <div>
          <dt>Сопоставлено</dt>
          <dd>{vm.summary.matched}</dd>
        </div>
        <div>
          <dt>Количество</dt>
          <dd>{vm.summary.quantity}</dd>
        </div>
        <div>
          <dt>Не распределено</dt>
          <dd>{vm.summary.unallocated}</dd>
        </div>
        <div>
          <dt>Сумма</dt>
          <dd>{vm.summary.amount}</dd>
        </div>
        <div>
          <dt>Документов в 1С</dt>
          <dd>{vm.summary.documents}</dd>
        </div>
      </dl>
      {vm.summary.blockers.length > 0 && (
        <ul className="onec-list" aria-label="Что мешает проведению">
          {vm.summary.blockers.map((blocker) => (
            <li key={blocker}>
              <p className="onec-problem">{blocker}</p>
            </li>
          ))}
        </ul>
      )}

      {!locked && (
        <details className="onec-section">
          <summary>
            <FileUp size={16} aria-hidden="true" /> Заполнить из накладной
          </summary>
          <p className="onec-muted">
            Файл загружается и проверяется в окне «Импорт накладной» этой базы; здесь — его идентификатор.
            {header.importedFileId !== null ? ` Уже привязан файл ${header.importedFileId}.` : ''}
          </p>
          <div className="onec-row">
            <InputField label="Идентификатор файла" value={vm.invoice.fileId} onChange={(fileId) => actions.invoiceChanged({ fileId })} />
            <SelectField
              label="Как заполнить"
              value={vm.invoice.mode}
              options={FILL_MODES}
              onChange={(mode) => actions.invoiceChanged({ mode: mode as FillMode })}
            />
          </div>
          {vm.invoice.fileId !== '' && vm.invoice.problem !== null && <p className="onec-problem">{vm.invoice.problem}</p>}
          <div className="onec-actions">
            <Button variant="secondary" onClick={actions.fillFromInvoice} disabled={vm.invoice.problem !== null}>
              Заполнить
            </Button>
          </div>
          {vm.fill !== null && (
            <div className="onec-screen">
              <p>
                «{vm.fill.fileName}»: {vm.fill.summary}
              </p>
              {vm.fill.skipped.length > 0 && (
                <ul className="onec-list" aria-label="Пропущенные строки накладной">
                  {vm.fill.skipped.map((item) => (
                    <li key={item.row}>
                      Строка {item.row}
                      {item.name !== null ? ` «${item.name}»` : ''}: {item.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </details>
      )}

      <Commands commands={vm.commands} onResend={actions.resend} onDismiss={actions.dismiss} />
      {vm.cloned !== null && (
        <div className="onec-actions">
          <Button variant="primary" onClick={actions.openClone}>
            Открыть копию {vm.cloned.number}
          </Button>
        </div>
      )}

      <div className="onec-tabs" role="tablist" aria-label="Разделы поставки">
        {SECTIONS.map((section) => (
          <button
            key={section.value}
            type="button"
            role="tab"
            aria-selected={vm.section === section.value}
            onClick={() => actions.sectionChosen(section.value)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">{children}</div>
    </section>
  )
}
